import { ColumnTypes, FilterTypes } from "commons/table/Constants";
import {
  QUERY_FILTER_TYPES,
  normalizeToStringArray,
} from "commons/utils/queryParamUtils";
import { RawConstants, FILTER } from "lookup/RawConstants";

export const RAW_LIST_HIDDEN_COLUMNS = {
  STD: ["referenceId"],
  DMR: ["customerNameAbbreviation", "clientNameAbbreviation", "switchNumber", "switchId", "globalSwitchId", "prescriberName", "memberNumberWithoutCustId", "projectedSavings", "effectiveDate"],
};

export const getRawListHiddenColumns = (faxProgramCode) =>
  RAW_LIST_HIDDEN_COLUMNS[faxProgramCode] ?? [];

export const RAW_LIST_COLUMNS = (
  {
    renderStatusCell,
    renderOwnerCell,
    renderLastModifiedDateCol,
    renderEffectiveDateCol,
    renderSavingsCell,
    statusFilterMap,
  },
  hiddenColumns = []
) => {
  const allColumns = [
    {
      header: "Cust",
      accessor: "customerNameAbbreviation",
      accessorKey: "customerNameAbbreviation",
      width: 60,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Client",
      accessor: "clientNameAbbreviation",
      accessorKey: "clientNameAbbreviation",
      width: 60,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Switch Id",
      accessor: "switchId",
      accessorKey: "switchId",
      width: 95,
      show: false,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Switch Id",
      accessor: "switchNumber",
      accessorKey: "switchNumber",
      width: 80,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Switch Id",
      accessor: "globalSwitchId",
      accessorKey: "globalSwitchId",
      width: 80,
      enableSorting: true,
      enableColumnFilter: true,
      show: false,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Ref ID",
      accessor: "referenceId",
      accessorKey: "referenceId",
      width: 150,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Status",
      accessor: "status",
      accessorKey: "status",
      width: 150,
      enableSorting: false,
      enableColumnFilter: true,
      columnType: ColumnTypes.Custom,
      filterType: FilterTypes.MultiSelect,
      filterOptions: statusFilterMap,
      customComponent: (value, row) => renderStatusCell(row),
    },
    {
      header: "Owner",
      accessor: "ownerName",
      accessorKey: "ownerName",
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Custom,
      filterType: FilterTypes.SearchableInput,
      customComponent: (value) => renderOwnerCell(value),
    },
    {
      header: "daysAfterLastModifiedDate",
      accessor: "daysAfterLastModifiedDate",
      accessorKey: "daysAfterLastModifiedDate",
      width: 95,
      show: false,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Last Modified",
      accessor: "lastModifiedDate",
      accessorKey: "lastModifiedDate",
      width: 125,
      enableSorting: true,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) =>
        renderLastModifiedDateCol(row.original.lastModifiedDate),
    },
    {
      header: "daysToEffectiveDate",
      accessor: "daysToEffectiveDate",
      accessorKey: "daysToEffectiveDate",
      width: 125,
      show: false,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Effective Date",
      accessor: "effectiveDate",
      accessorKey: "effectiveDate",
      width: 125,
      enableSorting: true,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) =>
        renderEffectiveDateCol(
          row.original.effectiveDate,
          row.original.daysToEffectiveDate
        ),
    },
    {
      header: "Prescriber",
      accessor: "prescriberName",
      accessorKey: "prescriberName",
      width: 180,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Member #",
      accessor: "memberNumberWithoutCustId",
      accessorKey: "memberNumberWithoutCustId",
      width: 110,
      enableSorting: false,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Projected Savings",
      accessor: "projectedSavings",
      accessorKey: "projectedSavings",
      enableSorting: true,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => renderSavingsCell(value),
    },
  ];

  return allColumns.filter((col) => !hiddenColumns.includes(col.accessorKey));
};

const mapStatusValuesFromUrl = (values = []) =>
  values.map((entry) => {
    const option = RawConstants.statusesMultiSelect.find(
      (status) => status.id === entry || status.name === entry
    );
    return option || { id: entry, name: entry };
  });

const parseRawStatusFromUrl = (rawValue) => {
  if (!Array.isArray(rawValue) || rawValue.length === 0) {
    return null;
  }
  if (rawValue.includes(RawConstants.DEFAULT_FILTER_CONST)) {
    return [RawConstants.DEFAULT_FILTER_CONST];
  }
  if (rawValue.includes("All Filters")) {
    return ["All Filters"];
  }
  return mapStatusValuesFromUrl(rawValue);
};

const omitRawStatusFromUrl = (filter) => {
  if (!filter?.value && filter?.value !== 0) {
    return true;
  }
  const currentValues = normalizeToStringArray(filter.value, ["id", "name"]);
  if (currentValues.includes(RawConstants.DEFAULT_FILTER_CONST)) {
    return true;
  }
  return (
    currentValues.length === FILTER.defaultBackend.value.length &&
    FILTER.defaultBackend.value.every((value) => currentValues.includes(value))
  );
};

export const RAW_URL_FILTER_CONFIG = [
  {
    filterId: "customerNameAbbreviation",
    paramKey: "customer",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
  {
    filterId: "clientNameAbbreviation",
    paramKey: "client",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
  {
    filterId: "switchNumber",
    paramKey: "switchNumber",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
  {
    filterId: "switchId",
    paramKey: "switchId",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
  {
    filterId: "globalSwitchId",
    paramKey: "globalSwitchId",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
  {
    filterId: "referenceId",
    paramKey: "refId",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
  {
    filterId: "status",
    paramKey: "status",
    valueType: QUERY_FILTER_TYPES.MULTI_SELECT,
    parseFromUrl: parseRawStatusFromUrl,
    omitFromUrlWhen: omitRawStatusFromUrl,
  },
  {
    filterId: "ownerName",
    paramKey: "owner",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
  {
    filterId: "daysAfterLastModifiedDate",
    paramKey: "daysAfterLastModified",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
  {
    filterId: "daysToEffectiveDate",
    paramKey: "daysToEffective",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
  {
    filterId: "prescriberName",
    paramKey: "prescriber",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
  {
    filterId: "memberNumberWithoutCustId",
    paramKey: "memberNumber",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
];
